import React, { useCallback, useContext, useEffect, useState } from "react";
import { Col, Dropdown, Form, Spinner } from "react-bootstrap";
import { fetchTypes, updateBookType } from "../../../http/bookAPI";
import Modal from "react-bootstrap/Modal";
import { Context } from "../../../index";
import { observer } from "mobx-react-lite";
import Row from "react-bootstrap/Row";

const UpdateBookType = observer(({ show, handleClose }) => {
    const { book } = useContext(Context);

    const [type, setType] = useState("");
    const [typeName, setTypeName] = useState("");
    const [typeTodo, setTypeTodo] = useState("");
    const [priceAddition, setPriceAddition] = useState("");
    const [priceShift, setPriceShift] = useState("");

    const [discount, setDiscount] = useState("");
    const [discountStart, setDiscountStart] = useState("");
    const [discountEnd, setDiscountEnd] = useState("");

    const [bonus, setBonus] = useState("");
    const [bonusStart, setBonusStart] = useState("");
    const [bonusEnd, setBonusEnd] = useState("");

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // ✅ Загружаем список типов книг при открытии модального окна
    useEffect(() => {
        if (show) {
            fetchTypes().then(data => book.setTypes(data));
        }
    }, [show]);

    // ✅ Очистка полей после обновления или закрытия окна
    const resetFields = () => {
        book.setSelectedBookType({});
        setType("");
        setTypeName("");
        setTypeTodo("");
        setPriceAddition("");
        setPriceShift("");
        setDiscount("");
        setDiscountStart("");
        setDiscountEnd("");
        setBonus("");
        setBonusStart("");
        setBonusEnd("");
    };

    // ✅ Оптимизированная функция обновления типа книги
    const modifyBookType = useCallback(async () => {
        if (!book.selectedBookType.id) {
            setError("Please select a book type to modify.");
            return;
        }

        setLoading(true);
        setError("");
        try {
            await updateBookType({
                id: book.selectedBookType.id,
                type: type || "",
                type_name: typeName || "",
                type_todo: typeTodo || "",
                book_type_price_addition: priceAddition || 0,
                book_type_price_shift: priceShift || 0,
                book_type_promotion_price_discount: discount || 0,
                book_type_promotion_price_discount_start_date: discountStart ? Math.floor(new Date(discountStart).getTime()) : null,
                book_type_promotion_price_discount_end_date: discountEnd ? Math.floor(new Date(discountEnd).getTime()) : null,
                book_type_promotion_price_bonus: bonus || 0,
                book_type_promotion_price_bonus_start_date: bonusStart ? Math.floor(new Date(bonusStart).getTime()) : null,
                book_type_promotion_price_bonus_end_date: bonusEnd ? Math.floor(new Date(bonusEnd).getTime()) : null,
            });

            const updatedBookTypes = await fetchTypes();
            book.setTypes(updatedBookTypes);
            resetFields();
            handleClose();
        } catch (e) {
            setError(e.response?.data?.message || "An error occurred while modifying the book type.");
        } finally {
            setLoading(false);
        }
    }, [book, type, typeName, typeTodo, priceAddition, priceShift, discount, discountStart, discountEnd, bonus, bonusStart, bonusEnd]);

    return (
        <Modal size="lg" show={show} onHide={() => { setError(""); resetFields(); handleClose(); }} centered>
            <div className="modal-content">
                <div className="modal-header">
                    <h1 className="modal-title fs-5">Edit Book Type:</h1>
                    <button type="button" className="btn-close" onClick={() => { setError(""); resetFields(); handleClose(); }}></button>
                </div>

                <div className="card">
                    <div className="card-body">
                        <div className="d-flex justify-content-center mt-1">
                            <Dropdown className="dropdown-center">
                                <Dropdown.Toggle variant="success" className="shadow">
                                    {book.selectedBookType.type_name || "Select Book Type to Modify"}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {book?.types?.map((bt) => (
                                        <Dropdown.Item
                                            key={bt.id}
                                            onClick={() => {
                                                book.setSelectedBookType(bt);
                                                setType(bt.type);
                                                setTypeName(bt.type_name);
                                                setTypeTodo(bt.type_todo);
                                                setPriceAddition(bt.book_type_price_addition);
                                                setPriceShift(bt.book_type_price_shift);
                                                setDiscount(bt.book_type_promotion_price_discount);
                                                setDiscountStart(bt.book_type_promotion_price_discount_start_date);
                                                setDiscountEnd(bt.book_type_promotion_price_discount_end_date);
                                                setBonus(bt.book_type_promotion_price_bonus);
                                                setBonusStart(bt.book_type_promotion_price_bonus_start_date);
                                                setBonusEnd(bt.book_type_promotion_price_bonus_end_date);
                                            }}
                                        >
                                            {bt.type_name}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>
                </div>

                {error && <div className="alert alert-danger mx-3">{error}</div>}

                <Row>
                    <Col md={6}>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-md-6">
                                    <Form className="mb-2">
                                        <div className="small ms-1 mt-1 mb-0 p-0">Book Type:</div>
                                        <Form.Control
                                            value={type}
                                            onChange={(e) => setType(e.target.value)}
                                            disabled={loading || !book.selectedBookType.id}
                                        />
                                    </Form>
                                </div>

                                <div className="col-md-6">
                                    <Form className="mb-2">
                                        <div className="small ms-1 mt-1 mb-0 p-0">Book Type Name:</div>
                                        <Form.Control
                                            value={typeName}
                                            onChange={(e) => setTypeName(e.target.value)}
                                            disabled={loading || !book.selectedBookType.id}
                                        />
                                    </Form>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6">
                                    <Form className="mb-2">
                                        <div className="small ms-1 mt-1 mb-0 p-0">Book Type ToDo:</div>
                                        <Form.Control
                                            value={typeTodo}
                                            onChange={(e) => setTypeTodo(e.target.value)}
                                            disabled={loading || !book.selectedBookType.id}
                                        />
                                    </Form>
                                </div>

                                <div className="col-md-6">
                                    <Form className="mb-2">
                                        <div className="small ms-1 mt-1 mb-0 p-0">Price Addition:</div>
                                        <Form.Control
                                            value={priceAddition}
                                            onChange={(e) => setPriceAddition(e.target.value)}
                                            disabled={loading || !book.selectedBookType.id}
                                        />
                                    </Form>
                                </div>
                            </div>


                        </div>

                        <div className="mt-3 p-4">
                            <img
                                src="./Virtalibry_logo_sub_no_bg.png"
                                alt="Virtalibry Logo"
                                width="305"
                                height="47"
                                className="d-flex align-top ms-3"
                            />
                        </div>
                    </Col>


                    <Col md={6}>

                        <div className="modal-body">
                            <div className="small ms-1 mt-1 mb-0 p-0">Update Promotion Discount:</div>
                            <Form className="mb-2">
                                <Form.Control
                                    type="number"
                                    value={discount}
                                    onChange={(e) => setDiscount(e.target.value)}
                                    disabled={loading || !book.selectedBookType.id}
                                />
                            </Form>

                            <div className="row">
                                <div className="col-md-6">
                                    <div className="small ms-1 mt-1 mb-0 p-0">Discount Start Date:</div>
                                    <Form className="mb-2">
                                        <Form.Control
                                            type="date"
                                            value={discountStart ? new Date(discountStart * 60000).toISOString().split("T")[0] : ""}
                                            onChange={(e) => setDiscountStart(Math.floor(Date.parse(e.target.value) / 60000))}
                                            disabled={loading || !book.selectedBookType.id}
                                        />
                                    </Form>
                                </div>

                                <div className="col-md-6">
                                    <div className="small ms-1 mt-1 mb-0 p-0">Discount End Date:</div>
                                    <Form className="mb-2">
                                        <Form.Control
                                            type="date"
                                            value={discountEnd ? new Date(discountEnd * 60000).toISOString().split("T")[0] : ""}
                                            onChange={(e) => setDiscountEnd(Math.floor(Date.parse(e.target.value) / 60000))}
                                            disabled={loading || !book.selectedBookType.id}
                                        />
                                    </Form>
                                </div>
                            </div>

                            <div className="small ms-1 mt-1 mb-0 p-0">Update Promotion Bonus:</div>
                            <Form className="mb-2">
                                <Form.Control
                                    type="number"
                                    value={bonus}
                                    onChange={(e) => setBonus(e.target.value)}
                                    disabled={loading || !book.selectedBookType.id}
                                />
                            </Form>

                            <div className="row">
                                <div className="col-md-6">
                                    <div className="small ms-1 mt-1 mb-0 p-0">Bonus Start Date:</div>
                                    <Form className="mb-2">
                                        <Form.Control
                                            type="date"
                                            value={bonusStart ? new Date(bonusStart * 60000).toISOString().split("T")[0] : ""}
                                            onChange={(e) => setBonusStart(Math.floor(Date.parse(e.target.value) / 60000))}
                                            disabled={loading || !book.selectedBookType.id}
                                        />
                                    </Form>
                                </div>

                                <div className="col-md-6">
                                    <div className="small ms-1 mt-1 mb-0 p-0">Bonus End Date:</div>
                                    <Form className="mb-2">
                                        <Form.Control
                                            type="date"
                                            value={bonusEnd ? new Date(bonusEnd * 60000).toISOString().split("T")[0] : ""}
                                            onChange={(e) => setBonusEnd(Math.floor(Date.parse(e.target.value) / 60000))}
                                            disabled={loading || !book.selectedBookType.id}
                                        />
                                    </Form>
                                </div>
                            </div>
                        </div>


                    </Col>
                </Row>

                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => { setError(""); resetFields(); handleClose(); }} disabled={loading}>Close</button>
                    <button className="btn btn-success" onClick={modifyBookType} disabled={loading}>{loading ? <Spinner as="span" animation="border" size="sm" /> : "Save Changes"}</button>
                </div>
            </div>
        </Modal>
    );
});

export default UpdateBookType;
